// Metadata for trigger types and action types

// Cadena de dependencia: unit -> lesson -> content -> exercise
// Cada trigger define hasta qué nivel de la cadena necesita llegar
export type TriggerDepth = 'none' | 'unit' | 'lesson' | 'content' | 'exercise'

export const TRIGGER_META: Record<string, {
  label: string
  icon: string
  description: string
  // Hasta qué nivel de la jerarquía necesita este trigger
  depth: TriggerDepth
  // Campos extra fuera de la jerarquía (number, badge, cohort, workshop)
  extraFields?: { key: string; label: string; type: 'number' | 'badge' | 'cohort' | 'workshop' }[]
}> = {
  student_completes_unit: {
    label: 'Alumno completa unidad',
    icon: '📦',
    description: 'Se activa cuando un alumno termina todos los contenidos de una unidad',
    depth: 'unit',
  },
  student_completes_lesson: {
    label: 'Alumno completa lección',
    icon: '📖',
    description: 'Se activa cuando un alumno termina una lección específica',
    depth: 'lesson',
  },
  student_completes_content: {
    label: 'Alumno completa contenido',
    icon: '📝',
    description: 'Se activa cuando un alumno termina un contenido específico',
    depth: 'content',
  },
  student_completes_exercise: {
    label: 'Alumno completa ejercicio',
    icon: '✏️',
    description: 'Se activa cuando un alumno termina un ejercicio específico',
    depth: 'exercise',
  },
  student_first_login: {
    label: 'Primer inicio de sesión',
    icon: '👋',
    description: 'Se activa la primera vez que un alumno inicia sesión',
    depth: 'none',
  },
  student_starts_session: {
    label: 'Alumno inicia sesión',
    icon: '🔑',
    description: 'Se activa cada vez que un alumno inicia sesión',
    depth: 'none',
  },
  student_passes_unit_exam: {
    label: 'Alumno aprueba examen de unidad',
    icon: '🎓',
    description: 'Se activa cuando aprueba el examen de una unidad',
    depth: 'unit',
  },
  student_fails_unit_exam: {
    label: 'Alumno reprueba examen de unidad',
    icon: '❌',
    description: 'Se activa cuando reprueba el examen de una unidad',
    depth: 'unit',
  },
  student_passes_final_exam: {
    label: 'Alumno aprueba examen final',
    icon: '🏆',
    description: 'Se activa cuando el alumno aprueba el examen final del curso',
    depth: 'none',
  },
  student_earns_badge: {
    label: 'Alumno gana insignia',
    icon: '🏅',
    description: 'Se activa cuando un alumno obtiene una insignia específica',
    depth: 'none',
    extraFields: [{ key: 'badge_id', label: 'Insignia', type: 'badge' }],
  },
  student_reaches_streak: {
    label: 'Alumno alcanza racha de días',
    icon: '🔥',
    description: 'Se activa cuando el alumno alcanza N días consecutivos de estudio',
    depth: 'none',
    extraFields: [{ key: 'streak_days', label: 'Días consecutivos', type: 'number' }],
  },
  student_reaches_points: {
    label: 'Alumno alcanza puntos',
    icon: '⭐',
    description: 'Se activa cuando el alumno acumula N puntos totales',
    depth: 'none',
    extraFields: [{ key: 'points_threshold', label: 'Puntos mínimos', type: 'number' }],
  },
  student_completes_course: {
    label: 'Alumno completa el curso',
    icon: '🎉',
    description: 'Se activa cuando el alumno termina todo el curso (4 unidades + examen final)',
    depth: 'none',
  },
  student_inactive: {
    label: 'Alumno inactivo',
    icon: '😴',
    description: 'Se activa cuando un alumno lleva N días sin entrar a la plataforma',
    depth: 'none',
    extraFields: [{ key: 'inactive_days', label: 'Días de inactividad', type: 'number' }],
  },
  student_joins_cohort: {
    label: 'Alumno se une a cohorte',
    icon: '👥',
    description: 'Se activa cuando un alumno es agregado a una cohorte',
    depth: 'none',
    extraFields: [{ key: 'cohort_id', label: 'Cohorte', type: 'cohort' }],
  },
  student_attends_workshop: {
    label: 'Alumno asiste a taller',
    icon: '🎤',
    description: 'Se activa cuando un alumno es marcado como presente en un taller',
    depth: 'none',
    extraFields: [{ key: 'workshop_id', label: 'Taller', type: 'workshop' }],
  },
  student_perfect_lesson: {
    label: 'Alumno perfecto en lección',
    icon: '💯',
    description: 'Se activa cuando un alumno saca 100% en todos los ejercicios de una lección',
    depth: 'lesson',
  },
  student_registers: {
    label: 'Nuevo alumno se registra',
    icon: '🆕',
    description: 'Se activa cuando un nuevo alumno se registra en la plataforma',
    depth: 'none',
  },
}

export const ACTION_META: Record<string, { label: string; icon: string; color: string; description: string }> = {
  send_email: {
    label: 'Enviar email',
    icon: '📧',
    color: 'bg-blue-50 text-[#0055A4] border-[#0055A4]/20',
    description: 'Envía un correo electrónico al alumno desde cfi.nomadlexis@gmail.com'
  },
  show_popup: {
    label: 'Mostrar popup',
    icon: '💬',
    color: 'bg-purple-50 text-purple-700 border-purple-200',
    description: 'Muestra un popup al alumno la próxima vez que entre a la plataforma'
  },
  send_survey: {
    label: 'Enviar encuesta',
    icon: '📋',
    color: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    description: 'Envia una encuesta al alumno como popup interactivo'
  },
  award_points: {
    label: 'Otorgar puntos',
    icon: '⭐',
    color: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    description: 'Agrega puntos bonus al alumno'
  },
  award_badge: {
    label: 'Otorgar insignia',
    icon: '🏅',
    color: 'bg-amber-50 text-amber-700 border-amber-200',
    description: 'Otorga una insignia específica al alumno'
  },
  send_notification: {
    label: 'Enviar notificación',
    icon: '🔔',
    color: 'bg-green-50 text-green-700 border-green-200',
    description: 'Envía una notificación interna al alumno'
  },
  send_whatsapp: {
    label: 'Enviar WhatsApp',
    icon: '📱',
    color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    description: 'Envía un mensaje de WhatsApp al alumno (requiere configuración)'
  },
  generate_certificate: {
    label: 'Generar certificado',
    icon: '📜',
    color: 'bg-rose-50 text-rose-700 border-rose-200',
    description: 'Genera un certificado para el alumno usando una plantilla personalizada'
  },
}

export const POPUP_ICONS = [
  { value: 'trophy', label: 'Trofeo', emoji: '🏆' },
  { value: 'star', label: 'Estrella', emoji: '⭐' },
  { value: 'fire', label: 'Fuego', emoji: '🔥' },
  { value: 'party', label: 'Fiesta', emoji: '🎉' },
  { value: 'rocket', label: 'Cohete', emoji: '🚀' },
  { value: 'heart', label: 'Corazón', emoji: '❤️' },
  { value: 'thumbsup', label: 'Pulgar arriba', emoji: '👍' },
  { value: 'clap', label: 'Aplausos', emoji: '👏' },
  { value: 'brain', label: 'Cerebro', emoji: '🧠' },
  { value: 'books', label: 'Libros', emoji: '📚' },
  { value: 'warning', label: 'Advertencia', emoji: '⚠️' },
  { value: 'info', label: 'Info', emoji: 'ℹ️' },
]

export const EMAIL_TEMPLATES = [
  { value: 'custom', label: 'Personalizado' },
  { value: 'welcome', label: 'Bienvenida' },
  { value: 'congrats', label: 'Felicitaciones' },
  { value: 'reminder', label: 'Recordatorio' },
  { value: 'inactive', label: 'Reactivacion' },
]
